import { useState } from 'react'
import { Link } from 'react-router-dom'
import { MegaphoneIcon } from '@heroicons/react/24/outline'
import { useUnreadAnnouncements } from '../../hooks/useUnreadAnnouncements'
import { isUnread, needsAcknowledgement } from '../../types/announcement'
import type { Announcement } from '../../types/announcement'
import AnnouncementRow from '../../components/announcements/AnnouncementRow'
import AnnouncementModal from '../../components/announcements/AnnouncementModal'

const MAX_ROWS = 4

// Surfaces the most urgent actionable announcements (unread or needing ack) on
// the dashboard. Renders nothing when there's nothing to act on — it should not
// add noise on a caught-up dashboard. Opening a row uses the same modal as the
// full feed; ack/read there refetch the shared cache entry (also the nav badge).
const AnnouncementsWidget = () => {
  const { announcements, unreadCount, loading } = useUnreadAnnouncements()
  const [selected, setSelected] = useState<Announcement | null>(null)

  const actionable = announcements
    .filter((a) => isUnread(a) || needsAcknowledgement(a))
    .slice(0, MAX_ROWS)

  // Keep the open modal in sync with refreshed data (read/ack markers clear).
  const selectedLive = selected
    ? announcements.find((a) => a.id === selected.id) ?? selected
    : null

  if (loading && announcements.length === 0) return null
  if (actionable.length === 0) return null

  return (
    <div className="rounded-2xl border border-gray-100 shadow-sm bg-white p-6">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MegaphoneIcon className="h-4 w-4 text-primary-500" />
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
            Announcements{unreadCount > 0 ? ` · ${unreadCount} unread` : ''}
          </h2>
        </div>
        <Link to="/announcements" className="text-xs font-medium text-primary-600 hover:text-primary-700">
          View all →
        </Link>
      </div>

      <div className="divide-y divide-gray-100">
        {actionable.map((a) => (
          <AnnouncementRow key={a.id} announcement={a} onOpen={setSelected} />
        ))}
      </div>

      <AnnouncementModal
        announcement={selectedLive}
        onClose={() => setSelected(null)}
      />
    </div>
  )
}

export default AnnouncementsWidget
